/** Values fixed by BRIEF.md. Admin-editable ones live in office_settings; these are the defaults. */
export const TIMEZONE = 'Asia/Kolkata';

export const ROLES = ['admin', 'worker', 'supervisor'] as const;
export type Role = (typeof ROLES)[number];

export const EMPLOYMENT_TYPES = ['full_time', 'part_time'] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const TARGET_MINUTES: Record<EmploymentType, number> = {
  full_time: 540,
  part_time: 300,
};

/**
 * How short a day has to be before anybody is told about it.
 *
 * The owner, 7 October 2026: "it is showing below target for even 8mins shortfall. we can ignore
 * <30mins shortfall." Eight minutes off a nine-hour day is somebody who walked to the gate, not
 * somebody who went home early — and a column that calls that Below Target is a column the office
 * learns to scroll past, which costs them the days that really were short.
 *
 * It changes what is *said*, never what is counted: the shortfall minutes are still the real
 * ones, and every hours total is untouched.
 */
export const BELOW_TARGET_GRACE_MINUTES = 30;

/** Short by enough to say so. Anything under the grace reads as a full day. */
export const isBelowTarget = (shortfallMinutes: number): boolean =>
  shortfallMinutes >= BELOW_TARGET_GRACE_MINUTES;

/**
 * Which of the day's two shifts a worker belongs to.
 *
 * A label, not a clock. This office has no start time to be late for — a full-time worker owes
 * nine hours and a part-time one five, whenever they are worked — so a shift does not change what
 * anybody is due. What it does is say who is on the floor alongside whom, and that is exactly the
 * thing the weekly-off caps are counting: three full-time people off on a Tuesday matters per
 * shift, because the shift is the group that has to cover the work.
 */
export const SHIFTS = ['first', 'second'] as const;
export type Shift = (typeof SHIFTS)[number];

/** '1st' / '2nd' — for a report column or a badge, where the word "shift" is already implied. */
export const SHIFT_SHORT: Record<Shift, string> = { first: '1st', second: '2nd' };
/** '1st Shift' / '2nd Shift' — for a line that stands on its own. */
export const SHIFT_LABEL: Record<Shift, string> = { first: '1st Shift', second: '2nd Shift' };

export const DEFAULT_OFFICE_RADIUS_M = 150;
/**
 * Location never blocks a punch. A fix with accuracy above this cannot say where the phone is,
 * so the punch is accepted as `location_unverified` and flagged for the admin.
 */
export const UNVERIFIED_ACCURACY_M = 500;
/** Seconds the Punch screen waits for the phone to reach a usable fix before using what it has. */
export const LOCATION_SETTLE_SECONDS = 30;
/** The worker app keeps the most accurate fix of this many recent seconds. */
export const LOCATION_BEST_FIX_WINDOW_SECONDS = 30;
/** Unverified-location punches in one calendar month that raise a dashboard flag for the phone. */
export const UNVERIFIED_PATTERN_THRESHOLD = 3;
export const PUNCH_RATE_LIMIT_SECONDS = 60;
export const PUNCH_CHALLENGE_TTL_SECONDS = 60;
/** Outside-geofence reports from the app (no passkey) are recorded at most this often per worker. */
export const PUNCH_REPORT_INTERVAL_SECONDS = 300;

/** Day boundary, local time. A punch before this hour belongs to the previous calendar day. */
/** 22:00 since 16 September 2026; was 04:00, a night-shift default this site never needed. */
export const DEFAULT_DAY_CUTOFF = '22:00';
export const DEFAULT_DIGEST_TIME = '22:00';
/** Office time at which the daily attendance report is emailed to admins. */
export const DEFAULT_DAILY_REPORT_TIME = '21:00';

export const INVITE_TTL_HOURS = 72;
export const ADMIN_SESSION_HOURS = 12;
/** "Keep me signed in on this device": the session is bound to a device cookie and lasts this long. */
export const ADMIN_REMEMBER_DAYS = 30;
/** Password reset links are single use and expire after this many minutes. */
export const PASSWORD_RESET_MINUTES = 30;
export const WORKER_SESSION_DAYS = 30;

export const DAY_STATUSES = [
  'present',
  'present_no_punchout',
  'absent',
  'weekly_off',
  'holiday',
] as const;
export type DayStatus = (typeof DAY_STATUSES)[number];

export const PUNCH_TYPES = ['in', 'out'] as const;
export type PunchType = (typeof PUNCH_TYPES)[number];

export const PUNCH_SOURCES = ['app', 'admin', 'auto_close'] as const;
export type PunchSource = (typeof PUNCH_SOURCES)[number];

/** Reason codes written to punch_attempts on rejection. Stable strings; the UI maps them to text. */
export const PUNCH_REJECT_REASONS = [
  'location_missing',
  'invalid_coordinates',
  'outside_geofence',
  'shift_already_open',
  'no_open_shift',
  'rate_limited',
  'challenge_missing',
  'challenge_expired',
  'assertion_invalid',
  'credential_revoked',
  'credential_mismatch',
  'user_inactive',
  /** Android said the position came from a mock provider. */
  'mock_location',
  /** Play Integrity would not vouch for the device or for the app being ours. */
  'integrity_failed',
  /** A worker known to have the app punched from somewhere that could not be checked. */
  'app_required',
  /** The app is older than the minimum this server will accept. */
  'app_too_old',
] as const;
export type PunchRejectReason = (typeof PUNCH_REJECT_REASONS)[number];

/**
 * Weekly off: Mon–Sun week, and an off day may fall on any of the seven.
 *
 * It was Tue–Sat until 28 September 2026, when the owner opened it up: their manual roster
 * already put people off on Sundays and Mondays, and the system was the thing disagreeing.
 * Cover is protected by the per-day caps, not by forbidding days.
 */
export const ALLOWED_OFF_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export const DEFAULT_OFF_CAPS = { total: 6, full_time: 3, part_time: 3 } as const;

export const FLAG_KINDS = [
  'weak_gps',
  'outside_geofence',
  'location_unverified',
  'location_unverified_pattern',
  'no_punchout',
  'credential_reuse',
  'revoked_credential_auth',
] as const;
export type FlagKind = (typeof FLAG_KINDS)[number];

/** Employee codes are allocated by the server: SIVA0101, SIVA0102, … They are never editable. */
export const EMPLOYEE_CODE_PREFIX = 'SIVA';

/**
 * The number the first worker gets. Not 1: the owner asked for codes to begin at 0101, so the
 * roster reads as a series rather than as a count of how few people there are.
 */
export const EMPLOYEE_CODE_START = 101;

/**
 * Format an allocated number as an employee code. Four digits, zero-padded, widening on its own
 * once the numbers pass 9999 — SIVA9999 is followed by SIVA10000, not by a code that has to be
 * renumbered. Codes are never reused, so a deactivated worker's code stays theirs.
 *
 * Was SL-001 until September 2026. The migration to this format is n + 100, so SL-001 became
 * SIVA0101 and every worker kept their place in the roster. Documents published before that keep
 * the old code in their snapshot, which is correct — it is what was issued.
 */
export function employeeCode(n: number): string {
  return `${EMPLOYEE_CODE_PREFIX}${String(n).padStart(4, '0')}`;
}
